<template>
  <div class="ps-discount-body">
    <!-- before discount -->
    <p class="ps-discount__sub">
      {{
        $t("before_discount", {
          amount: formatMoney(totalBeforeDiscount),
        })
      }}
    </p>

    <!-- type pills -->
    <div class="ps-discount__pills">
      <button
        type="button"
        class="ps-discount__pill"
        :class="{ 'ps-discount__pill--active': localDiscountType === 'percentage' }"
        @click="localDiscountType = 'percentage'"
      >
        {{ $t("percentage") }}
      </button>
      <button
        type="button"
        class="ps-discount__pill"
        :class="{ 'ps-discount__pill--active': localDiscountType === 'amount' }"
        @click="localDiscountType = 'amount'"
      >
        {{ $t("amount") }}
      </button>
    </div>

    <!-- input -->
    <div class="ps-discount__field">
      <label class="ps-discount__label">{{
        localDiscountType === "percentage"
          ? $t("discount_percentage")
          : $t("discount_kip")
      }}</label>
      <input
        :value="discountDisplay"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :maxlength="localDiscountType === 'percentage' ? 3 : 12"
        :placeholder="localDiscountType === 'percentage' ? '0 - 100' : '0'"
        class="ps-discount__input"
        @input="onInput"
      />
    </div>

    <div class="ps-discount__presets">
      <button
        v-for="preset in presets"
        :key="preset"
        type="button"
        class="ps-discount__preset"
        :class="{ 'ps-discount__preset--active': Number(localAmount) === preset }"
        @click="pickPreset(preset)"
      >
        {{
          localDiscountType === "percentage"
            ? preset + "%"
            : formatMoney(preset)
        }}
      </button>
    </div>

    <p v-if="localDiscountType === 'percentage'" class="ps-discount__hint">
      {{ $t("max_100") }}
    </p>
    <p v-if="validationError" class="ps-discount__error">
      {{ validationError }}
    </p>

    <button
      type="button"
      class="ps-discount__apply"
      :disabled="!canApply"
      @click="applyDiscount"
    >
      {{ $t("apply") }}
    </button>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "DiscountModal",
  mixins: [myMixin],
  props: {
    value: { type: Boolean, default: false },
    totalBeforeDiscount: { type: Number, required: true },
    discountType: { type: String, default: "percentage" },
    initialAmount: { type: String, default: "0" },
    currencySymbol: { type: String, default: "₭" },
    showTotal: { type: Boolean, default: true },
    showPreview: { type: Boolean, default: true },
    submitButtonText: { type: String, default: "apply" },
  },
  data() {
    return {
      localDiscountType: this.discountType || "percentage",
      // raw digits only — formatted for display; percentage capped at 100
      localAmount: String(Number(this.initialAmount) || 0),
      validationError: null,
    };
  },
  computed: {
    presets() {
      return this.localDiscountType === "percentage"
        ? [5, 10, 15, 20]
        : [10000, 50000, 100000, 500000];
    },
    discountDisplay() {
      if (!this.localAmount) return "";
      let n = Number(this.localAmount) || 0;
      if (this.localDiscountType === "percentage") n = Math.min(n, 100);
      return this.localDiscountType === "percentage"
        ? String(n)
        : n.toLocaleString("en-US");
    },
    discountAmount() {
      const value = parseFloat(this.localAmount) || 0;
      if (this.localDiscountType === "percentage") {
        return (this.totalBeforeDiscount * value) / 100;
      }
      return Math.min(value, this.totalBeforeDiscount);
    },
    totalAfterDiscount() {
      return Math.max(this.totalBeforeDiscount - this.discountAmount, 0);
    },
    canApply() {
      const value = parseFloat(this.localAmount) || 0;
      return value > 0 && this.discountAmount > 0 && !this.validationError;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) this.reset();
    },
    localDiscountType() {
      this.localAmount = "";
      this.validationError = null;
    },
    localAmount() {
      this.validateDiscount();
    },
    discountType(newVal) {
      this.localDiscountType = newVal;
    },
    initialAmount(newVal) {
      this.localAmount = String(Number(newVal) || 0);
    },
  },
  methods: {
    formatMoney(n) {
      const v = Number(n) || 0;
      return `${v.toLocaleString("en-US")} ${this.currencySymbol}`;
    },
    pickPreset(preset) {
      this.localAmount = String(preset);
    },
    onInput(e) {
      let digits = String(e.target.value || "")
        .replace(/\D/g, "")
        .slice(0, 9);
      if (this.localDiscountType === "percentage") {
        digits = String(Math.min(Number(digits) || 0, 100));
      }
      this.localAmount = digits;
    },
    validateDiscount() {
      const value = parseFloat(this.localAmount) || 0;
      this.validationError = null;
      if (value <= 0) return;
      if (this.localDiscountType === "percentage") {
        if (value > 100) {
          this.validationError = this.$t("discount_percentage_cannot_exceed_100");
        }
      } else if (value > this.totalBeforeDiscount) {
        this.validationError = this.$t("discount_amount_cannot_exceed_total");
      }
    },
    applyDiscount() {
      if (!this.canApply) return;
      const discountData = {
        type: this.localDiscountType,
        value: parseFloat(this.localAmount),
        amount: this.discountAmount,
        totalAfterDiscount: this.totalAfterDiscount,
      };
      this.$emit("apply", discountData);
      this.$emit("close");
    },
    reset() {
      this.localAmount = String(Number(this.initialAmount) || 0);
      this.localDiscountType = this.discountType || "amount";
      this.validationError = null;
    },
  },
};
</script>

<style scoped>
.ps-discount-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 4px;
}

.ps-discount__sub {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-slate-400);
}

.ps-discount__pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ps-discount__pill {
  padding: 12px;
  border-radius: 16px;
  font-weight: 900;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: var(--ps-slate-500);
  transition: all 0.15s ease;
}
.ps-discount__pill:hover {
  border-color: var(--ps-brand);
}
.ps-discount__pill--active {
  background: var(--ps-brand);
  border-color: var(--ps-brand);
  color: #fff;
}

.ps-discount__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ps-discount__label {
  font-size: 12px;
  font-weight: 900;
  color: var(--ps-slate-500);
}

.ps-discount__input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #141021;
  border-radius: 12px;
  background: var(--ps-cream);
  font-weight: 900;
  font-size: 1.125rem;
  font-variant-numeric: tabular-nums;
  color: var(--ps-ink);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.ps-discount__input:focus {
  outline: none;
  background: #fff;
  border-color: var(--ps-brand);
}

.ps-discount__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: -8px;
}

.ps-discount__preset {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: var(--ps-slate-500);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease,
    background-color 0.15s ease;
}
.ps-discount__preset:hover {
  border-color: var(--ps-brand);
  color: var(--ps-brand);
}
.ps-discount__preset--active {
  background: var(--ps-brand);
  border-color: var(--ps-brand);
  color: #fff;
}

.ps-discount__hint {
  margin: -6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-slate-400);
}

.ps-discount__error {
  margin: -6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
}

.ps-discount__apply {
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  background: var(--ps-brand, #5b39c9);
  color: #fff;
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  box-shadow: none;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}
.ps-discount__apply:hover:not(:disabled) {
  background: #4c2eb5;
}
.ps-discount__apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
